<template>
  <div class="zoom-overlay">
    <!-- Zoomable Media Container -->
    <div
      class="overlay"
      ref="zoomContainer"
      @wheel="overlayWheelZoom"
      @mousedown="overlayMouseDown"
      @touchstart="overlayTouchStart"
      @touchmove="overlayTouchMove"
      @touchend="overlayTouchEnd"
      @gesturestart="overlayGestureStart"
      @gesturechange="overlayGestureChange"
      @gestureend="overlayGestureEnd"
      :class="{ zoomed: isZoomed }"
    >
      <img
        v-if="!isVideo"
        v-pixelated-scale="pixelatedScaleBinding"
        ref="zoomedImg"
        :src="media"
        alt="Zoomable Media"
        class="overlay-media"
        :style="mediaStyle"
        draggable="false"
        @load="onLoadImg(media)"
        @dblclick="imageDoubleClick"
      />
      <video
        v-else
        ref="zoomedVideo"
        :src="media"
        class="overlay-media"
        :style="mediaStyle"
        @loadedmetadata="onLoadVideo(media)"
      ></video>
      <button
        class="close-btn"
        v-bind="closeBtnListeners"
        aria-label="Close"
        title="Close"
      >
        <X />
      </button>
      <button
        class="rotate-btn"
        v-bind="rotateBtnListeners"
        aria-label="Rotate"
        title="Rotate"
      >
        <RotateCw />
      </button>
      <button
        class="pixelated-btn"
        @click="cyclePixelatedScale()"
        :aria-label="`Pixelated rendering: ${pixelatedScaleMode}`"
        :title="`Pixelated rendering: ${pixelatedScaleMode}`"
      >
        <span v-if="pixelatedScaleMode === 'on'">Px</span>
        <span v-else-if="pixelatedScaleMode === 'gif'">GIF</span>
        <span v-else>Sm</span>
      </button>
      <button
        class="overlay-prev"
        @click="showPreviousMedia"
        aria-label="Previous"
        title="Previous"
      >
        <ChevronLeft />
      </button>
      <button
        class="overlay-next"
        @click="showNextMedia"
        aria-label="Next"
        title="Next"
      >
        <ChevronRight />
      </button>
    </div>

    <!-- Video controls: fixed at the bottom, outside the pan/zoom transform -->
    <VideoControls
      v-if="isVideo"
      :video="zoomedVideo"
      :src="media"
      class="zoom-video-controls"
    />

    <div
      id="debug-overlay"
      v-show="!debugOverlayHidden"
      @click="debugOverlayHidden = false"
    >
      <!-- press "D" to show, or long-press the X of the image viewer -->
      <p>Initial Scale: <span class="debug-initial-scale">{{ initialScale.toFixed(2) }}</span></p>
      <p>Current Scale: <span class="debug-current-scale">{{ scale.toFixed(2) }}</span></p>
      <p>At Initial Scale: <span class="debug-at-initial-scale">{{ isZoomed ? "yes" : "no" }}</span></p>
      <p>VelocityX: <span class="debug-velocity-x">{{ velocityX.toFixed(2) }}</span></p>
      <p>TranslateX: <span class="debug-translate-x">{{ translateX.toFixed(2) }}</span></p>
      <p><a @click="translateX -= 10">&larr; Move</a> | <a @click="translateX += 10">Move &rarr;</a></p>
      <p>TranslateMinX: <span class="debug-inertia-min-x">{{ translateMinX.toFixed(2) }}</span></p>
      <p>TranslateMaxX: <span class="debug-inertia-max-x">{{ translateMaxX.toFixed(2) }}</span></p>
      <p>VelocityY: <span class="debug-velocity-y">{{ velocityY.toFixed(2) }}</span></p>
      <p>TranslateY: <span class="debug-translate-y">{{ translateY.toFixed(2) }}</span></p>
      <p><a @click="translateY -= 10">&uarr; Move</a> | <a @click="translateY += 10">Move &darr;</a></p>
      <p>TranslateMinY: <span class="debug-inertia-min-y">{{ translateMinY.toFixed(2) }}</span></p>
      <p>TranslateMaxY: <span class="debug-inertia-max-y">{{ translateMaxY.toFixed(2) }}</span></p>
      <p>Aspect Ratio: <span class="debug-inertia-max-y">{{ props.aspectRatio.toFixed(4) }}</span></p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {
  ref,
  computed,
  watch,
  onMounted,
  onUnmounted,
  nextTick, toRef,
} from "vue";
import { type RotationModes, useRotator } from "@/composables/AspectRatioRotator";
import {awaitableRequestAnimationFrame} from "@/utils/awaitableRequestAnimationFrame";
import { useHoldable } from "@/composables/useHoldable";
import {useCycle} from "@/composables/useCycle";
import { usePointerLock } from "@vueuse/core";
import { ChevronLeft, ChevronRight, RotateCw, X } from "lucide-vue-next";
import { vPixelatedScale } from "@/directives/pixelatedScale";
import VideoControls from "@/components/slides/VideoControls.vue";
import {
  getLocalStorageItem,
  setLocalStorageItem,
  SETTINGS_PIXELATED_SCALE_MODE,
  SETTINGS_ZOOM_PLAYBACK_SYNC,
} from "@/stores/localStorage";



// Props and Emits
interface Props {
  media: string;
  mimeType: string;
  totalMedia: number;
  currentIndex: number;
  aspectRatio: number;
  /** The outer (slide) video element — used to sync position and playback state. */
  outerVideo?: HTMLVideoElement | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  (event: "close"): void;
  (event: "prev"): void;
  (event: "next"): void;
}>();

// Tracks whether the outer player was playing when zoom opened, so we can restore it on close.
let outerWasPlaying = false;
// Captures the sync setting at open time so close behaviour matches what was set on open.
let syncPlaybackOnOpen = false;

// Elements
const zoomContainer = ref<HTMLDivElement | null>(null);
const zoomedImg = ref<HTMLImageElement | null>(null);
const zoomedVideo = ref<HTMLVideoElement | null>(null);
const zoomedMediaElement = computed(() =>
  isVideo.value ? zoomedVideo.value : zoomedImg.value
);

// Pointer lock for infinite panning
const { lock, unlock, isSupported: pointerLockSupported } = usePointerLock();

// DOM Elements
const body = document.body;

// Constants
const scrollMaxScale = 5;
const inertiaFriction = 0.95;
const outOfBoundsMinSpeed = 2; // How fast it will bounce back
const boundsSafety = 40; // Minimum space from the edges of the viewport, in px

// State Variables
const scale = ref(1);
const initialScale = ref(1);
const naturalWidth = ref(1);
const naturalHeight = ref(1);
const isZoomed = ref(false);
const translateX = ref(0);
const translateY = ref(0);
const isDragging = ref(false);
const startX = ref(0);
const startY = ref(0);
const pinchStartX = ref(0);
const pinchStartY = ref(0);
const lastTranslateX = ref(0);
const lastTranslateY = ref(0);
const velocityX = ref(0);
const velocityY = ref(0);
const pinchStartDistance = ref(0);
const translateMinX = ref(0);
const translateMaxX = ref(0);
const translateMinY = ref(0);
const translateMaxY = ref(0);
const debugOverlayHidden = ref(true);

// Computed Properties
const isVideo = computed(() => props.mimeType?.startsWith("video"));
const isGif = computed(() => props.mimeType === "image/gif");

type PixelatedScaleMode = "on" | "gif" | "off";
const {
  state: pixelatedScaleMode,
  next: cyclePixelatedScale,
  set: setPixelatedScaleMode,
} = useCycle<PixelatedScaleMode>(["on", "gif", "off"]);
setPixelatedScaleMode(getLocalStorageItem(SETTINGS_PIXELATED_SCALE_MODE, "gif") as PixelatedScaleMode);
watch(pixelatedScaleMode, (newVal) => setLocalStorageItem(SETTINGS_PIXELATED_SCALE_MODE, newVal));

const pixelatedScaleBinding = computed(() => {
  if (pixelatedScaleMode.value === "on") return true;
  if (pixelatedScaleMode.value === "gif") return isGif.value;
  return false;
});

const {
  state: rotatorMode,
  next: rotate,
  set: setRotateMode,
} = useCycle<RotationModes>(
  [
    // "portrait",
    // "landscape",
    -90, 0, 90, 180,
  ], [
    'auto',
  ],
  {
    initialValue: 0,
  }
);

const {
  transform: rotatorTransform,
  updateRotation,
} = useRotator(
    zoomedMediaElement,
    true,
    rotatorMode,
    true,
    toRef(props, 'aspectRatio'),
);
watch(
    () => [props.currentIndex, props.media, props.mimeType, props.currentIndex],
    async () => {
      await nextTick();
      await awaitableRequestAnimationFrame();
      console.log("ZoomOverlay: Media changed, updating rotation and scale.", { aspectRatio: props.aspectRatio });
      updateRotation();
    },
);
const mediaStyle = computed(() => ({
  transform: `translate(${translateX.value}px, ${translateY.value}px) scale(${scale.value}) ${rotatorTransform.value}`,
  // transition: isDragging.value ? "none" : "transform 0.2s",
}));

// Functions, lots of them
function onLoadImg(mediaSrc: string) {
  if (mediaSrc !== props.media) {
    console.log("Wrong media, ignoring image load.");
    return;
  }
  naturalWidth.value = zoomedImg.value.naturalWidth;
  naturalHeight.value = zoomedImg.value.naturalHeight;
  mediaGeneralizedOnLoadCalculations();
}

function onLoadVideo(mediaSrc: string) {
  if (mediaSrc !== props.media) {
    console.log("Wrong media, ignoring video load.");
    return;
  }
  naturalWidth.value = (zoomedVideo.value as HTMLVideoElement).videoHeight;
  naturalHeight.value = (zoomedVideo.value as HTMLVideoElement).videoHeight;
  mediaGeneralizedOnLoadCalculations();

  // Sync with outer player: seek to its position and take over playback.
  syncPlaybackOnOpen = getLocalStorageItem(SETTINGS_ZOOM_PLAYBACK_SYNC, true);
  if (syncPlaybackOnOpen && props.outerVideo && zoomedVideo.value) {
    outerWasPlaying = !props.outerVideo.paused;
    zoomedVideo.value.currentTime = props.outerVideo.currentTime;
    if (outerWasPlaying) {
      props.outerVideo.pause();
      zoomedVideo.value.play().catch(() => {});
    }
  }
}

function mediaGeneralizedOnLoadCalculations() {
  const viewportWidth = zoomContainer.value.clientWidth;
  const viewportHeight = zoomContainer.value.clientHeight;

  // Calculate the initial fit scale
  const scaleWidth = viewportWidth / naturalWidth.value;
  const scaleHeight = viewportHeight / naturalHeight.value;
  initialScale.value = Math.min(scaleWidth, scaleHeight, 1); // Fit scale
  resetTransformations();
}

function closeOverlay() {
  // Restore outer player: seek to zoom's position and resume if it was playing.
  if (syncPlaybackOnOpen && isVideo.value && props.outerVideo && zoomedVideo.value) {
    props.outerVideo.currentTime = zoomedVideo.value.currentTime;
    if (outerWasPlaying) {
      props.outerVideo.play().catch(() => {});
    }
  }
  resetTransformations();
  emit("close");
}

function setZoomedState(newIsZoomed: boolean | null = null) {
  if (newIsZoomed === undefined || newIsZoomed === null) {
    newIsZoomed = scale.value !== initialScale.value;
  }
  isZoomed.value = newIsZoomed;
}

// Function to create or update a visual indicator for the bounding rectangle
function showBoundingRect(rect: DOMRect, x: number, y: number, trX: number, trY: number) {
  // Check if the indicator already exists
  let indicator = document.getElementById("bounding-rect-indicator");

  if (!indicator) {
    // Create a new div element if it doesn't exist
    indicator = document.createElement("div");
    indicator.id = "bounding-rect-indicator"; // Set an ID for easy reference
    indicator.style.position = "absolute";
    indicator.style.border = "2px solid red";
    indicator.style.pointerEvents = "none"; // Make sure it doesn't interfere with mouse events
    indicator.style.zIndex = "99999";
    const divX = document.createElement("div");
    const divY = document.createElement("div");
    const divX2 = document.createElement("div");
    const divY2 = document.createElement("div");
    [divX, divY, divX2, divY2].forEach((div: HTMLDivElement, index: number) => {
      div.style.position = index < 2 ? "absolute" : "fixed";
      // eslint-disable-next-line prettier/prettier
      div.style.pointerEvents = "none"; // Make sure it doesn't interfere with mouse events
      indicator.appendChild(div);
    });
    document.body.appendChild(indicator);
  }

  // Update the styles for the indicator
  indicator.style.left = `${rect.left}px`;
  indicator.style.top = `${rect.top}px`;
  indicator.style.width = `${rect.width}px`;
  indicator.style.height = `${rect.height}px`;
  const lookup = {
    // "index": [top/left, bottom&right, w, h, color]
    // eslint-disable-next-line prettier/prettier
    0: ["0", "", `${x * 100}%`, "100%", true, "rgba(255, 0, 0, 0.5)"], // Semi-transparent red
    // eslint-disable-next-line prettier/prettier
    1: ["0", "", "100%", `${y * 100}%`, true, "rgba(0, 255, 0, 0.5)"], // Semi-transparent green
    // eslint-disable-next-line prettier/prettier
    2: ["50%", "", `${trX}px`, "4px", false, "rgba(0, 0, 255, 0.5)"], // Semi-transparent blue
    // eslint-disable-next-line prettier/prettier
    3: ["", "50%", "4px", `${trY}px`, false, "rgba(255, 255, 255, 0.5)"], // Semi-transparent yellow
  };
  Array.from(indicator.getElementsByTagName("div")).forEach((div, index) => {
    div.style.top = lookup[index][0];
    div.style.left = lookup[index][0];
    div.style.bottom = lookup[index][1];
    div.style.right = lookup[index][1];
    div.style.width = lookup[index][2];
    div.style.height = lookup[index][3];
    div.style.backgroundColor = lookup[index][5];
    if (!lookup[index][4]) {
      div.style.border = `1px solid ${lookup[index][5]}`;
    }
  });
}

// Zoom and Drag
async function overlayWheelZoom(e: WheelEvent) {
  e.preventDefault();

  // Normalise wheel delta across modes (pixel / line / page) so trackpads feel smooth
  // and mouse wheels give a consistent step regardless of OS scroll acceleration.
  const rawDelta =
    e.deltaMode === 1 ? e.deltaY * 30   // line mode  → px equivalent
    : e.deltaMode === 2 ? e.deltaY * 300 // page mode  → px equivalent
    : e.deltaY;                           // pixel mode (default)

  // Exponential zoom: each "standard" wheel tick (~100 px) changes scale by ~10 %.
  // This feels consistent at any zoom level and respects fine trackpad gestures.
  const factor = Math.exp(-rawDelta * 0.001);
  const newScale = Math.min(
    scrollMaxScale,
    Math.max(initialScale.value, scale.value * factor)
  );
  const actualFactor = newScale / scale.value; // may differ due to clamping

  // Mouse position relative to the current image centre (screen px).
  // Derivation: image centre is at (viewport_centre + translate), so:
  //   centredMouse = mouseScreenPos - imageCentreScreenPos
  const rect = zoomedMediaElement.value.getBoundingClientRect();
  const centeredMouseX = e.clientX - (rect.left + rect.width / 2);
  const centeredMouseY = e.clientY - (rect.top + rect.height / 2);

  // Keep the pixel under the cursor fixed during zoom.
  // Proof: point at image-local coords p = centredMouse / oldScale.
  // After zoom we need: centredMouse = p * newScale + ΔT
  //   → ΔT = centredMouse * (1 - newScale/oldScale) = centredMouse * (1 - actualFactor)
  translateX.value += centeredMouseX * (1 - actualFactor);
  translateY.value += centeredMouseY * (1 - actualFactor);
  scale.value = newScale;

  // When fully zoomed back to the fit scale, snap translate to centre.
  if (newScale < initialScale.value) {
    translateX.value = 0;
    translateY.value = 0;
    lastTranslateX.value = 0;
    lastTranslateY.value = 0;
  }

  setZoomedState();
}

function overlayMouseDown(e: MouseEvent) {
  // reset inertia
  velocityX.value = 0;
  velocityY.value = 0;
  isDragging.value = true;
  startX.value = e.clientX - lastTranslateX.value;
  startY.value = e.clientY - lastTranslateY.value;
  if (isZoomed.value && pointerLockSupported.value && zoomContainer.value) {
    lock(zoomContainer.value);
  }
  e.preventDefault();
}

function documentMouseUp() {
  isDragging.value = false;
  if (pointerLockSupported.value) {
    unlock();
  }
  // applyInertia();
}

function documentMouseMove(e: MouseEvent) {
  if (!isDragging.value) return;

  if (document.pointerLockElement && e.movementX !== undefined) {
    // Pointer is locked: accumulate delta movement (unbounded, works past screen edges)
    translateX.value += e.movementX;
    translateY.value += e.movementY;
  } else {
    // Fallback: absolute position tracking
    translateX.value = e.clientX - startX.value;
    translateY.value = e.clientY - startY.value;
  }

  velocityX.value = translateX.value - lastTranslateX.value;
  velocityY.value = translateY.value - lastTranslateY.value;

  lastTranslateX.value = translateX.value;
  lastTranslateY.value = translateY.value;
  // Update the transformation with the new position
}

// Touch-based Drag and Zoom
function overlayTouchStart(e: TouchEvent) {
  if (e.touches.length === 1 && !isZoomed.value) {
    startX.value = e.touches[0].clientX;
    return;
  }
  if (e.touches.length === 1) {
    // Start dragging
    isDragging.value = true;
    // Touch point is start point for panning
    startX.value = e.touches[0].clientX - lastTranslateX.value;
    startY.value = e.touches[0].clientY - lastTranslateY.value;
  } else if (e.touches.length === 2) {
    // Start pinch-zooming
    isDragging.value = false;
    pinchStartDistance.value = getPinchDistance(e.touches);
    // Midpoint is start point for panning
    pinchStartX.value = (e.touches[0].clientX + e.touches[1].clientX) / 2;
    pinchStartY.value = (e.touches[0].clientY + e.touches[1].clientY) / 2;
  }
}

function overlayTouchMove(e: TouchEvent) {
  if (isDragging.value && e.touches.length === 1) {
    // Panning with one finger
    translateX.value = e.touches[0].clientX - startX.value;
    translateY.value = e.touches[0].clientY - startY.value;
    velocityX.value = translateX.value - lastTranslateX.value;
    velocityY.value = translateY.value - lastTranslateY.value;

    lastTranslateX.value = translateX.value;
    lastTranslateY.value = translateY.value;
  } else if (e.touches.length === 2) {
    // Zooming with two fingers and panning
    const pinchCurrentDistance = getPinchDistance(e.touches);
    const zoomAmount = pinchCurrentDistance / pinchStartDistance.value;
    scale.value = Math.max(
      initialScale.value,
      Math.min(5, scale.value * zoomAmount)
    );
    setZoomedState();

    // Calculate panning movement based on finger movement
    const pinchCurrentX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
    const pinchCurrentY = (e.touches[0].clientY + e.touches[1].clientY) / 2;

    translateX.value += pinchCurrentX - pinchStartX.value;
    translateY.value += pinchCurrentY - pinchStartY.value;

    pinchStartDistance.value = pinchCurrentDistance;
    pinchStartX.value = pinchCurrentX;
    pinchStartY.value = pinchCurrentY;

    updateDragBounds();
  }
}

function overlayTouchEnd(/* e: TouchEvent */) {
  if (!isZoomed.value) {
    const deltaX = e.changedTouches[0].clientX - startX.value;
    if (Math.abs(deltaX) > 50 && isZoomed.value && !isKeys) {
      // delta is high enough velocity, and prevent navigation if zoomed in
      if (deltaX > 0) {
        showPreviousMedia();
      } else {
        showNextMedia();
      }
    }
    return;
  }
  isDragging.value = false;
  applyInertia();
}

function imageDoubleClick() {
  if (!isZoomed.value) {
    scale.value = initialScale.value * 2;
  } else {
    scale.value = initialScale.value;
  }
  setZoomedState();
  updateDragBounds();
}

// Pinch distance calculation
function getPinchDistance(touches: TouchList) {
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
}

function resetTransformations() {
  velocityX.value = 0;
  velocityY.value = 0;
  translateX.value = 0;
  translateY.value = 0;
  lastTranslateX.value = 0;
  lastTranslateY.value = 0;
  scale.value = initialScale.value;
  setZoomedState(false);

  updateDragBounds();
}

// Make sure the image won't go out of bounds:
// Update the drag bounds based on the current scale and viewport dimensions
function updateDragBounds() {
  if (zoomContainer.value === undefined || zoomContainer.value === null) {
    return;
  }
  const viewportWidth = zoomContainer.value.clientWidth;
  const viewportHeight = zoomContainer.value.clientHeight;


  const imgWidth = naturalWidth.value * scale.value; // Width of the image scaled
  const imgHeight = naturalHeight.value * scale.value; // Height of the image scaled

  // taken *2 as everything is half the size.
  const doubledBoundsSafety = boundsSafety * 2;

  // For translateX (left-right)
  translateMaxX.value = (viewportWidth + imgWidth) / +2 - doubledBoundsSafety; // Image cannot move beyond the right
  translateMinX.value = (viewportWidth + imgWidth) / -2 + doubledBoundsSafety; // Image cannot move beyond the left

  // For translateY (up-down)
  translateMaxY.value = (viewportHeight + imgHeight) / +2 - doubledBoundsSafety; // Image cannot move beyond the bottom
  translateMinY.value = (viewportHeight + imgHeight) / -2 + doubledBoundsSafety; // Image cannot move beyond the top
}

// Apply inertia
function applyInertia() {
  function step() {
    let isStopped =
      Math.abs(velocityX.value) < 0.5 && Math.abs(velocityY.value) < 0.5;
    // If the image is too far out of bounds, apply a "bounce back" effect
    if (translateX.value > translateMaxX.value) {
      if (!isStopped && velocityX.value > 0) {
        velocityX.value *= -1; // Bounce back to the left
      } else {
        velocityX.value = Math.min(
          outOfBoundsMinSpeed * -1,
          velocityX.value,
          translateMaxX.value - translateX.value
        );
        isStopped = false;
      }
    } else if (translateX.value < translateMinX.value) {
      // -101, -50 => -50 - -101 = 51 => -101 + -51
      if (!isStopped && velocityX.value < 0) {
        velocityX.value *= -1; // Bounce back to the right
      }
      velocityX.value = Math.max(
        outOfBoundsMinSpeed * +1,
        velocityX.value,
        translateMinX.value - translateX.value
      );
      isStopped = false;
    }

    if (translateY.value > translateMaxY.value) {
      if (!isStopped && velocityY.value > 0) {
        velocityY.value *= -1; // Bounce back to the left
      }
      velocityY.value = Math.min(
        outOfBoundsMinSpeed * -1,
        velocityY.value,
        translateMaxY.value - translateY.value
      );
      isStopped = false;
    } else if (translateY.value < translateMinY.value) {
      if (!isStopped && velocityY.value < 0) {
        velocityY.value *= -1; // Bounce back to the bottom
      }
      velocityY.value = Math.max(
        outOfBoundsMinSpeed * +1,
        velocityY.value,
        translateMinY.value - translateY.value
      );
      isStopped = false;
    }

    // Apply the velocity to translate position
    translateX.value += velocityX.value;
    translateY.value += velocityY.value;

    // If the velocity is low enough, stop the animation
    if (isStopped) {
      lastTranslateX.value = translateX.value;
      lastTranslateY.value = translateY.value;
      return;
    }

    // Apply friction
    velocityX.value *= inertiaFriction;
    velocityY.value *= inertiaFriction;

    requestAnimationFrame(step);
  }

  step();
}

watch(
  () => isZoomed.value,
  (newIsZoom) => (newIsZoom ? enableZoomMode() : disableZoomMode())
);

// Prevent page movement when zooming, prevent pull-to-refresh
function enableZoomMode() {
  body.classList.add("zoom-open");
}

function disableZoomMode() {
  body.classList.remove("zoom-open");
}

// Ensure Prevent Default on Touch Events - Prevents page movement when zooming.
function overlayGestureStart(e: TouchEvent) {
  e.preventDefault(); // Stop the page from zooming
}

function overlayGestureChange(e: TouchEvent) {
  e.preventDefault(); // Handle custom zooming logic instead
}

function overlayGestureEnd(e: TouchEvent) {
  e.preventDefault(); // Finalize zoom state
}

// Debug window
function toggleDebugOverlay() {
  debugOverlayHidden.value = !debugOverlayHidden.value;
}

function documentKeyDown(e: KeyboardEvent) {
  // Don't intercept keys while the user is typing in an input/textarea
  if (
    e.target instanceof HTMLInputElement ||
    e.target instanceof HTMLTextAreaElement ||
    (e.target instanceof HTMLElement && e.target.isContentEditable)
  ) return;

  switch (e.key) {
    case "Escape": // Close overlay
      closeOverlay();
      e.preventDefault();
      e.stopPropagation();
      break;
    case "ArrowLeft": // Navigate to the previous image
      showPreviousMedia();
      e.preventDefault();
      e.stopPropagation();
      break;
    case "ArrowRight": // Navigate to the next image
      showNextMedia();
      e.preventDefault();
      e.stopPropagation();
      break;
    case "d":
      toggleDebugOverlay();
      e.preventDefault();
      e.stopPropagation();
      break;
  }
}

const { listeners: closeBtnListeners } = useHoldable(
  (e) => closeOverlay(), // onClick
  (e) => toggleDebugOverlay() // onHold
);

const { listeners: rotateBtnListeners } = useHoldable(
  (e) => rotate(), // onClick
  (e) => setRotateMode("auto") // onHold
);

function showPreviousMedia() {
  emit("prev");
}

function showNextMedia() {
  emit("next");
}

function onMountedHook() {
  document.addEventListener("mouseup", documentMouseUp);
  document.addEventListener("mousemove", documentMouseMove);
  document.addEventListener("keydown", documentKeyDown);
  if (isZoomed.value) {
    enableZoomMode();
  }
}

function onUnMountedHook() {
  document.removeEventListener("keydown", documentKeyDown);
  document.removeEventListener("mousemove", documentMouseMove);
  document.removeEventListener("mouseup", documentMouseUp);
  disableZoomMode();
}

onMounted(onMountedHook);
onUnmounted(onUnMountedHook);
</script>

<style scoped>
/* Overlay styles */
.zoom-overlay {
  z-index: 10;
}
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--bg-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

/* Video controls: sit above the overlay, pinned to the bottom of the viewport */
.zoom-video-controls {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 11;
}

.overlay.hidden {
  display: none;
}

.overlay-media {
  max-width: 80%;
  max-height: 80%;
  border-radius: 10px;
}

.overlay.zoomed .overlay-media {
  cursor: grab;
}
.overlay.zoomed .overlay-media:active {
  cursor: grabbing;
}

.close-btn, .rotate-btn, .pixelated-btn {
  position: absolute;
  top: 40px;
  font-size: 2rem;
  background: none;
  border: none;
  color: var(--text-primary);
  cursor: pointer;
  transition: color var(--transition-fast);
  &:hover { color: var(--accent); }
  &.close-btn {
    right: 20px;
  }
  &.rotate-btn {
    left: 20px;
    font-size: 1.5rem;
  }
  &.pixelated-btn {
    left: 70px;
    font-size: 1rem;
    font-weight: bold;
  }
}

.overlay-prev, .overlay-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  font-size: 2rem;
  color: var(--text-primary);
  cursor: pointer;
  transition: color var(--transition-fast);
  &:hover { color: var(--accent); }
}

.overlay-prev {
  left: 20px;
}

.overlay-next {
  right: 20px;
}

/* Overlay styles (updated for zoom/drag) */
.overlay-media {
  max-width: none;
  max-height: none;
  width: auto;
  height: auto;
}

#debug-overlay {
  position: fixed;
  top: 10px;
  right: 10px;
  background-color: var(--bg-surface);
  color: var(--text-primary);
  border: 1px solid var(--border);
  padding: 10px;
  border-radius: var(--radius-md);
  font-size: 14px;
  z-index: 1001;
}

#debug-overlay.hidden {
  display: none;
}

.debug-overlay-toggle {
  user-select: none; /* Prevent text selection */
  -webkit-user-select: none; /* Safari support */
  -ms-user-select: none; /* IE support */
  pointer-events: auto; /* Ensure it can still be clicked */
}

.debug-overlay-toggle:active {
  pointer-events: none; /* Temporarily disable interactions during long-press */
}

.debug-overlay-close {
  cursor: pointer; /* Indicate it's clickable */
}

.debug-overlay-close:active {
  background-color: rgba(255, 0, 0, 0.2); /* Optional: Visual feedback on tap */
}
</style>
<style>
/* un-scoped global style */

/* Prevent over-scrolling and page zooming */
body.zoom-open {
  overflow: hidden; /* Prevent scrolling */
  touch-action: none; /* Disable default touch gestures */
  overscroll-behavior: none; /* prevent pull-to-refresh */
}
</style>
